import Vue from 'vue';
import Vuex from 'vuex';
import residentialFlow, { stepMiniTankChoise, stepHeaterChoise } from '@/utils/residential-flow';
import commercialFlow from '@/utils/commercial-flow';
import { Icon, latLng } from 'leaflet';
import { getLocationsWithTemp, getProducts } from '@/api/eemaxApi';

Vue.use(Vuex);

export const FLOW_TYPE = {
  RESIDENTIAL: 'RESIDENTIAL',
  COMMERCIAL: 'COMMERCIAL',
  SPECIAL: 'SPECIAL',
};

export const defaultIcon = new Icon({
  iconUrl: require('@/assets/img/ico-marker-shadow.svg'),
  iconSize: [33, 40],
  iconAnchor: [21, 30],
});
export const selectedIcon = new Icon({
  iconUrl: require('@/assets/img/ico-marker-selected.svg'),
  iconSize: [21, 30],
  iconAnchor: [15, 30],
});

const miniTanksProductFamilyId = '9424c736-316d-451c-a2a8-2d9c8f653194';

const miniTanksCapacity = {
  EMT1: 1.5,
  'EMT2.5': 2.5,
  EMT4: 4,
  EMT6: 6,
};

export const baseUrl = process.env.NODE_ENV === 'development' ? process.env.VUE_APP_API_HOST : window.location.origin;

export const commonFixtures = [
  { title: 'Standard Handwash Sink', flowRate: '~1 - 1.5 GPM', img: `${process.env.VUE_APP_PATH}/img/f_SHS.jpg` },
  { title: 'Standard Flow Shower', flowRate: '~1.5 - 2.5 GPM', img: `${process.env.VUE_APP_PATH}/img/f_SFS.jpg` },
  { title: 'Kitchen Sink', flowRate: '~1.5 - 2.2 GPM', img: `${process.env.VUE_APP_PATH}/img/f_KS.jpg` },
  { title: 'Washing Machine', flowRate: '~1 - 2 GPM', img: `${process.env.VUE_APP_PATH}/img/f_WM.jpg` },
  { title: 'High Efficiency Dishwasher', flowRate: '~1.5 - 2 GPM', img: `${process.env.VUE_APP_PATH}/img/f_HED.jpg` },
  { title: 'Bathtub', flowRate: '~2 - 4 GPM', img: `${process.env.VUE_APP_PATH}/img/f_B.jpg` },
];

export const initBreadcrumbs = [
  { name: 'Home', href: '/' },
  { name: 'Help center', href: '/' },
];

const urlParams = new URLSearchParams(window.location.search);
const isDevMode = urlParams.get('dev-mode') || false;

const initState = {
  currentQuestionId: null,
  selectedProduct: null,
  selectedCabinet: null,
  selectedCabinetOptions: [],
  isLoading: false,
  selectedZip: null,
  steps: [],
  products: [],
  flow: null,
  selectedFlow: null,
  markers: [],
  initBreadcrumbs,
};

export default new Vuex.Store({
  state: {
    isMobile: false,
    containerHeight: 'auto',
    isDevMode,
    ...initState,
  },
  mutations: {
    setIsMobile(state, isMobile) {
      state.isMobile = isMobile;
    },
    setSelectedFlow(state, value) {
      state.selectedFlow = value;
      if (value === FLOW_TYPE.RESIDENTIAL) {
        state.flow = residentialFlow;
      }
      if (value === FLOW_TYPE.COMMERCIAL) {
        state.flow = commercialFlow;
      }
      if (state.flow && state.flow.length > 0) {
        state.currentQuestionId = state.flow[0].id;
        state.steps = [{ id: state.flow[0].id, name: state.flow[0].step }];
      }
    },
    setMarkers(state, value) {
      state.markers = value;
    },
    setSelectedZip(state, value) {
      state.selectedZip = value;
    },
    changeQuestion(state, value) {
      if (!value) {
        state.selectedFlow = null;
        state.currentQuestionId = null;
        state.flow = null;
      } else {
        state.currentQuestionId = value;
      }
    },
    setSteps(state, value) {
      state.steps = value;
    },
    setBreadcrumbs(state, value) {
      state.initBreadcrumbs = value;
    },
    setProducts(state, value) {
      state.products = value;
    },
    setContainerHeight(state, value) {
      state.containerHeight = value;
    },
    setIsLoading(state, value) {
      state.isLoading = value;
    },
    setSelectedProduct(state, value) {
      state.selectedProduct = value;
    },
    setSelectedCabinet(state, value) {
      state.selectedCabinet = value;
    },
    setSelectedCabinetOptions(state, value) {
      state.selectedCabinetOptions = value;
    },
    resetAll(state) {
      Object.entries(initState).forEach(([key, value]) => {
        state[key] = value;
      });
    },
  },
  actions: {
    setIsMobile({ commit }, value) {
      commit('setIsMobile', value);
    },
    setSelectedFlow({ commit }, value) {
      commit('setSelectedFlow', value);
    },
    setSelectedZip({ commit }, value) {
      commit('setSelectedZip', value);
    },
    async getProducts({ getters, state, commit }, withoutChoice = true) {
      if (state.products.length === 0) {
        commit('setIsLoading', true);
        const data = await getProducts(getters.filter);
        let products = data?.models || [];
        products = products.map((product) => {
          const specs = product.Documents.find((doc) => doc.PairingType.toLowerCase().indexOf('specification') > -1);
          const manual = product.Documents.find((doc) => doc.PairingType.toLowerCase().indexOf('manual') > -1);
          const documents = [];
          if (manual) {
            documents.push(manual);
          }
          if (specs) {
            documents.push(specs);
          }
          let capacity = 0;
          if (getters.filter['product-family'] && getters.filter['product-family'] === miniTanksProductFamilyId) {
            capacity = miniTanksCapacity[product.ModelSKU];
          }
          return {
            ...product,
            quantity: withoutChoice ? 1 : 0,
            img: product.model_img || product.group_img,
            model: `${product.ProductName} ${product.ModelSKU}`,
            documents,
            url: `${baseUrl}${product.url}`,
            capacity,
            dimensions: `${product.UnitWidth32}"W, ${product.UnitHeight32}"H, ${product.UnitDepth32}"D, ${product.WaterConnection}"NPT`,
            selected: false,
            nemaCabinets: product.NemaCabinets ? product.NemaCabinets.split(',').map((item) => item.trim()) : [],
          };
        });
        commit('setProducts', products);
        commit('setIsLoading', false);
      }
    },
    clearProducts({ commit }) {
      commit('setProducts', []);
    },
    async getMarkers({ commit }) {
      const data = await getLocationsWithTemp();
      const markers = data.map((marker) => ({
        id: marker.ID,
        title: marker.City,
        state: marker.State,
        zipCode: marker.Zip,
        temp: (+marker.Temp).toFixed(0),
        selected: false,
        latLng: latLng(marker.Lat, marker.Lon),
        icon: defaultIcon,
        ref: `marker_${marker.ID}`,
      }));
      commit('setMarkers', markers);
    },
    nextQuestion({ commit, state }, id) {
      commit('changeQuestion', id);
      const steps = [...state.steps];
      commit('setSteps', [...steps, { id, name: state.flow?.find((item) => item.id === id)?.step || null }]);
    },
    previousQuestion({ commit, state }) {
      if (state.steps.length > 0) {
        const steps = [...state.steps];
        steps.pop();
        if (steps[steps.length - 1]?.filter) {
          steps[steps.length - 1].filter = {};
        }
        const id = steps[steps.length - 1]?.id || null;
        commit('changeQuestion', id);
        commit('setSteps', steps);
      } else {
        commit('changeQuestion', null);
      }
      commit('setProducts', []);
    },
    addParamToFilter({ commit, state }, value) {
      const steps = [...state.steps];
      const filter = steps[steps.length - 1].filter || {};
      steps[steps.length - 1].filter = { ...filter, [value.name]: value.value };
      commit('setSteps', steps);
    },
    goToStep({ commit, state }, id) {
      let steps = [];
      if (id) {
        steps = [...state.steps];
        const index = steps.findIndex((step) => step.id === id);
        steps = steps.slice(0, index + 1);
        if (steps[steps.length - 1]?.filter) {
          steps[steps.length - 1].filter = {};
        }
      }
      commit('changeQuestion', id);
      commit('setSteps', steps);
      commit('setProducts', []);
    },
    addBreadcrumbs({ commit, state }, item) {
      const breadcrumbs = [...state.initBreadcrumbs, item];
      commit('setBreadcrumbs', breadcrumbs);
    },
    removeBreadcrumbs({ commit, state }) {
      const breadcrumbs = [...state.initBreadcrumbs];
      commit('setBreadcrumbs', breadcrumbs.pop());
    },
    setBreadcrumbs({ commit }, value) {
      commit('setBreadcrumbs', value);
    },
    setContainerHeight({ commit }, value) {
      commit('setContainerHeight', value);
    },
    changeProductQuantity({ commit, state }, { ModelID, quantity }) {
      let products = [...state.products];
      products = products.map((product) => {
        if (product.ModelID === ModelID) {
          return {
            ...product,
            quantity,
          };
        }
        return product;
      });
      commit('setProducts', products);
    },
    setSelectedProduct({ commit }, product) {
      commit('setSelectedProduct', product);
    },
    setSelectedCabinet({ commit }, cabinet) {
      commit('setSelectedCabinet', cabinet);
    },
    setSelectedCabinetOptions({ commit }, value) {
      commit('setSelectedCabinetOptions', value);
    },
    resetAll({ commit }) {
      commit('resetAll');
    },
  },
  getters: {
    currentQuestion(state) {
      return state.flow?.find((item) => item.id === state.currentQuestionId) || null;
    },
    breadcrumbs(state) {
      const result = [...state.initBreadcrumbs, ...state.steps];
      return result.length > 5 ? [...(result.slice(0, 1)), { name: '...' }, ...(result.slice(result.length - 4))] : result;
    },
    filter(state) {
      return state.steps.reduce((acc, step) => ({ ...acc, ...step.filter }), {});
    },
    showQuantity(state) {
      return state.selectedFlow === FLOW_TYPE.RESIDENTIAL && state.steps.find((step) => step.id === stepHeaterChoise || step.id === stepMiniTankChoise);
    },
    cabinetOptions(state) {
      return state.selectedCabinetOptions.join(' ');
    },
  },
});
